import unescapedText from 'I18n/utils/unescapedText';
import { Breadcrumbs } from 'foundations-components/transitional/components/Breadcrumbs';
import { useLocation, useParams } from 'quartz-routing/react-router';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { Paths } from '../../constants/paths';
import { useLocationHistory } from '../../context/LocationHistoryContext';
import { useRouteReferrer } from '../../context/RouteReferrerContext';
import { getBreadcrumbConfig } from '../constants/breadcrumbConfig';
import { useBreadcrumbs } from '../hooks/useBreadcrumbs';
import { RouteReferrerBackLink } from './RouteReferrerBackLink';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function getDefaultBackPath(pathname, productType) {
  switch (pathname) {
    case Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS:
      return Paths.SOLUTIONS_PARTNERS;
    case Paths.SEARCH_RESULTS_PRODUCTS:
      if (productType === 'APP') return Paths.APPS;
      if (productType === 'AGENT') return Paths.AGENTS;
      return Paths.HOME;
    default:
      return Paths.HOME;
  }
}
export function StorefrontBreadcrumbs({
  title,
  pathKey,
  backBreadcrumb,
  productType
}) {
  const referrer = useRouteReferrer();
  const {
    collectionSlug,
    listingSlug
  } = useParams();
  const items = useBreadcrumbs({
    title,
    pathKey,
    productType,
    slug: collectionSlug || listingSlug
  });
  const {
    getBackLinkLocation
  } = useLocationHistory();
  const backLinkLocation = getBackLinkLocation();
  const {
    pathname
  } = useLocation();
  let content;

  // Prioritize navigation history over the referrer. When users navigate within
  // the marketplace (e.g., from /explore to /listing/foo), the back link should
  // go to the previous page, not the original referrer. The referrer back link
  // is only shown on the landing page where there's no prior navigation history.
  if (backBreadcrumb && backLinkLocation) {
    content = /*#__PURE__*/_jsx(RouterLink, {
      to: backLinkLocation,
      "data-test-id": "storefront-back-breadcrumb",
      children: /*#__PURE__*/_jsx(StorefrontBreadcrumbMessage, {
        backBreadcrumb: true
      })
    });
  } else if (referrer) {
    content = /*#__PURE__*/_jsx(RouteReferrerBackLink, {
      referrer: referrer
    });
  } else if (backBreadcrumb) {
    content = /*#__PURE__*/_jsx(RouterLink, {
      to: getDefaultBackPath(pathname, productType),
      "data-test-id": "storefront-back-breadcrumb",
      children: /*#__PURE__*/_jsx(StorefrontBreadcrumbMessage, {
        backBreadcrumb: true
      })
    });
  } else {
    content = items === null || items === void 0 ? void 0 : items.map((item, index) => {
      const itemConfig = item.pathKey ? getBreadcrumbConfig()[item.pathKey] : undefined;
      const testId = itemConfig ? itemConfig.testId : 'storefront-breadcrumb-dynamic';
      return /*#__PURE__*/_jsx("span", {
        "data-test-id": testId,
        children: /*#__PURE__*/_jsx(StorefrontBreadcrumbItem, {
          item: item
        })
      }, `${item.pathKey}-${index}`);
    });
  }
  return /*#__PURE__*/_jsx(Breadcrumbs, {
    flush: true,
    marginBlockStart: "0",
    "data-test-id": "storefront-breadcrumbs",
    children: content
  });
}
function StorefrontBreadcrumbItem({
  item
}) {
  if (item.isCurrentPage) {
    return /*#__PURE__*/_jsx(StorefrontBreadcrumbMessage, {
      title: item.title,
      pathKey: item.pathKey
    });
  }
  if (item.pathKey) {
    return /*#__PURE__*/_jsx(RouterLink, {
      to: Paths[item.pathKey],
      children: /*#__PURE__*/_jsx(StorefrontBreadcrumbMessage, {
        title: item.title,
        pathKey: item.pathKey
      })
    });
  }
  return null;
}
function StorefrontBreadcrumbMessage({
  backBreadcrumb,
  pathKey,
  title
}) {
  if (backBreadcrumb) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: unescapedText('marketplaceStorefront.breadcrumbs.back')
    });
  }
  const config = pathKey ? getBreadcrumbConfig()[pathKey] : undefined;
  if (config) {
    return /*#__PURE__*/_jsx(Text, {
      as: "span",
      children: config.text
    });
  }
  return title || '';
}